import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import walletService from '../services/walletService';

interface BuyInModalProps {
  sessionMode?: boolean; // home game: join is free, host banks chips
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number) => void;
  minBuyIn: number;
  maxBuyIn: number;
  currency: 'playcoins' | 'pkcash';
  userId: string;
  /** Shown instead of the slider when the table has one fixed stack (all-in or fold). */
  fixedStackNote?: string;
}

const BuyInModal: React.FC<BuyInModalProps> = ({
  sessionMode = false,
  isOpen,
  onClose,
  onConfirm,
  minBuyIn,
  maxBuyIn,
  currency,
  userId,
  fixedStackNote,
}) => {
  const [amount, setAmount] = useState(minBuyIn);
  // One fixed stack: nothing to choose, so no slider to drag
  const isFixedStack = !!fixedStackNote || minBuyIn === maxBuyIn;
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !userId) return;
    
    const fetchBalance = async () => {
      setLoading(true);
      try {
        const wallet = await walletService.getWallet(userId);
        const bal = currency === 'playcoins' ? wallet.playCoins : wallet.pkCash;
        setBalance(bal);
        // Start at minimum or max if balance is lower
        setAmount(Math.max(minBuyIn, Math.min(minBuyIn, bal)));
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBalance();
  }, [isOpen, userId, currency, minBuyIn]);

  if (!isOpen) return null;

  const effectiveMax = Math.min(maxBuyIn, balance);
  // Snap the slider to big-blind-sized steps (min buy-in is 20xBB on
  // standard tables). Without a step the browser default is 1, which made
  // dragging land on amounts like 4,018.
  const buyInStep = Math.max(1, Math.round(minBuyIn / 20));
  const snapAmount = (v: number) =>
    Math.min(effectiveMax, Math.max(minBuyIn, Math.round(v / buyInStep) * buyInStep));
  const canBuyIn = balance >= minBuyIn && amount >= minBuyIn && amount <= effectiveMax;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '16px',
        padding: '24px',
        width: '90%',
        maxWidth: '360px',
        border: '1px solid #334155',
      }}>
        <h2 style={{ color: '#fff', margin: '0 0 8px', fontSize: '20px' }}>
          {isFixedStack ? 'Sit down' : 'Select Buy-In Amount'}
        </h2>

        <p style={{ color: '#94a3b8', margin: '0 0 20px', fontSize: '14px' }}>
          {isFixedStack
            ? `Everyone at this table starts with ${minBuyIn.toLocaleString()}`
            : `Min: ${minBuyIn.toLocaleString()} / Max: ${maxBuyIn.toLocaleString()}`}
        </p>
        
        {loading ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
            Loading balance...
          </p>
        ) : sessionMode ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <p style={{ color: '#a78bfa', fontWeight: 700, margin: '0 0 6px' }}>Home game</p>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 16px' }}>
              Joining is free. You start with the table minimum and the host manages chips.
            </p>
            <button
              onClick={() => onConfirm(minBuyIn)}
              style={{ width: '100%', padding: '14px 0', borderRadius: '10px', border: 'none',
                cursor: 'pointer', fontWeight: 800, fontSize: '15px', color: '#fff',
                background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)' }}
            >
              Join Free
            </button>
          </div>
        ) : balance < minBuyIn ? (
          <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
            <p style={{ color: '#ef4444', margin: '0 0 6px' }}>
              Insufficient {currency === 'playcoins' ? 'PlayCoins' : 'PSS Coins'}.
            </p>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 16px' }}>
              You have {balance.toLocaleString()}, need {minBuyIn.toLocaleString()}.
            </p>
            {/* Recovery path: don't dead-end an invited player. Their spot
                here (invite included) is remembered so topping up brings
                them straight back. */}
            <button
              onClick={() => {
                try {
                  localStorage.setItem('pss:returnToTable', JSON.stringify({
                    url: window.location.pathname + window.location.search,
                    ts: Date.now(),
                  }));
                } catch { /* best effort */ }
                navigate('/wallet');
              }}
              style={{
                width: '100%', padding: '14px 0', borderRadius: '10px', border: 'none',
                cursor: 'pointer', fontWeight: 800, fontSize: '15px', color: '#062128',
                background: 'linear-gradient(135deg, #00d9d9, #0891b2)',
              }}
            >
              Get {currency === 'playcoins' ? 'PlayCoins' : 'PSS Coins'}
            </button>
            <p style={{ color: '#64748b', fontSize: '12px', marginTop: '10px' }}>
              We will bring you right back to this table afterward
            </p>
          </div>
        ) : isFixedStack ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#22d3ee', lineHeight: 1.1 }}>
                {minBuyIn.toLocaleString()}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>fixed stack</div>
            </div>
            {fixedStackNote && (
              <p style={{
                color: '#e2e8f0', fontSize: '14px', lineHeight: 1.5, margin: '0 0 16px',
                background: 'rgba(236,72,153,0.12)', border: '1px solid rgba(236,72,153,0.4)',
                borderRadius: '10px', padding: '10px 12px',
              }}>
                {fixedStackNote}
              </p>
            )}
            <p style={{ color: '#64748b', textAlign: 'center', fontSize: '13px', margin: '0 0 20px' }}>
              Your balance: {balance.toLocaleString()} {currency === 'playcoins' ? 'PlayCoins' : 'PSS Coins'}
            </p>
          </>
        ) : (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <span style={{ fontSize: '24px' }}>
                {currency === 'playcoins' ? '' : ''}

              </span>
              <span style={{
                fontSize: '36px',
                fontWeight: 'bold',
                color: '#22d3ee',
              }}>
                {amount.toLocaleString()}
              </span>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setAmount(a => snapAmount(a - buyInStep))}
                disabled={amount <= minBuyIn}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#334155',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: amount <= minBuyIn ? 'not-allowed' : 'pointer',
                  opacity: amount <= minBuyIn ? 0.5 : 1,
                }}
              >
                -
              </button>
              
              <input
                type="range"
                min={minBuyIn}
                max={effectiveMax}
                step={buyInStep}
                value={amount}
                onChange={(e) => setAmount(snapAmount(Number(e.target.value)))}
                style={{
                  flex: 1,
                  height: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              
              <button
                onClick={() => setAmount(a => snapAmount(a + buyInStep))}
                disabled={amount >= effectiveMax}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#334155',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: amount >= effectiveMax ? 'not-allowed' : 'pointer',
                  opacity: amount >= effectiveMax ? 0.5 : 1,
                }}
              >
                +
              </button>
            </div>
            
            <p style={{ color: '#64748b', textAlign: 'center', fontSize: '13px', margin: '0 0 20px' }}>
              Your balance: {balance.toLocaleString()} {currency === 'playcoins' ? 'PlayCoins' : 'PSS Coins'}
            </p>
          </>
        )}
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '8px',
              border: '1px solid #475569',
              background: 'transparent',
              color: '#94a3b8',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={() => canBuyIn && onConfirm(amount)}
            disabled={!canBuyIn || loading}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '8px',
              border: 'none',
              background: canBuyIn ? 'linear-gradient(135deg, #f97316, #ea580c)' : '#334155',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: canBuyIn ? 'pointer' : 'not-allowed',
              opacity: canBuyIn ? 1 : 0.5,
            }}
          >
            TAKE SEAT
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyInModal;